import type { SeedIsland } from './seedLatin'

/**
 * The German expansion: the two situations where a prepared block is worth most.
 *
 * On the phone there is no face to read, no pointing, no writing it down — the
 * one place where "I'll manage with gestures" stops being an option, and
 * therefore the place a rehearsed opening earns its keep fastest.
 *
 * Officialdom is the other. Anyone actually living in Germany meets the Amt
 * within a fortnight, the vocabulary is narrow and fixed, and the cost of
 * fumbling it is another appointment six weeks out. It is also the island a
 * phrasebook never has, because phrasebooks are written for tourists.
 *
 * Notes are dense here on purpose. German's grammar punishes you in ways that
 * are invisible in the English — a case ending, a separable prefix landing at
 * the end of the sentence — and a note that explains why a line is shaped the
 * way it is turns one block into a pattern you can reuse.
 */
export const EXTRA_DE: SeedIsland[] = [
  {
    slug: 'telefon',
    title: 'On the phone',
    nativeTitle: 'Am Telefon',
    useWhen:
      'The one conversation you cannot get through on gestures. Rehearse it until the opening is automatic.',
    tags: ['transactional', 'phone'],
    level: 3,
    blocks: [
      [
        'Guten Tag, hier spricht Darin Molnar.',
        'Hello, this is Darin Molnar speaking.',
        'The standard German telephone opening. "hier spricht" — literally "here speaks" — is what you say instead of "this is". Give your surname; Germans usually answer with theirs alone.',
      ],
      [
        'Könnten Sie das bitte wiederholen? Die Verbindung ist schlecht.',
        'Could you repeat that, please? The line is bad.',
        'Blaming the line is the polite escape hatch — it asks for a repeat without announcing that your German failed. Worth having ready before you need it.',
      ],
      [
        'Ich rufe an, weil ich einen Termin vereinbaren möchte.',
        "I'm calling because I'd like to make an appointment.",
        'weil sends the verb to the end: "...vereinbaren möchte", not "möchte vereinbaren". Steal the whole frame — "Ich rufe an, weil..." opens almost any call.',
      ],
      [
        'Können Sie bitte etwas langsamer sprechen?',
        'Could you speak a little more slowly, please?',
        'Worth more than any other sentence on this island. Say it early rather than after you have already lost the thread.',
      ],
      [
        'Können Sie mir das bitte per E-Mail schicken?',
        'Could you send that to me by email, please?',
        'The move that saves a phone call going wrong: get it in writing. mir is dative — the thing goes *to me*.',
      ],
      [
        'Vielen Dank für Ihre Hilfe. Auf Wiederhören.',
        'Thank you for your help. Goodbye.',
        'Auf Wiederhören, not Auf Wiedersehen — you hear each other on the phone, you do not see each other. Using the wrong one marks you instantly.',
      ],
    ],
  },
  {
    slug: 'amt',
    title: 'Officialdom',
    nativeTitle: 'Auf dem Amt',
    useWhen:
      'Anyone living in Germany meets this within a fortnight. Narrow vocabulary, high cost of fumbling it.',
    tags: ['transactional', 'bureaucracy'],
    level: 3,
    blocks: [
      [
        'Ich möchte mich anmelden. Ich bin gerade umgezogen.',
        "I'd like to register. I've just moved.",
        'Die Anmeldung is the registration every resident must do. anmelden is reflexive — you register *yourself*, so mich is not optional.',
      ],
      [
        'Ich habe einen Termin um zehn Uhr auf den Namen Molnar.',
        "I have an appointment at ten o'clock under the name Molnar.",
        'auf den Namen — accusative after auf here, because it answers "onto what". The whole phrase is fixed; learn it as one lump.',
      ],
      [
        'Welche Unterlagen brauchen Sie von mir?',
        'Which documents do you need from me?',
        'Unterlagen is always plural in this sense. Asking before you are asked saves the second appointment.',
      ],
      [
        'Das habe ich leider nicht dabei. Kann ich es nachreichen?',
        "I'm afraid I don't have that with me. Can I submit it later?",
        'dabei = on my person. nachreichen — reach it in afterwards — is the exact word the clerk expects, and asking for it is normal rather than a favour.',
      ],
      [
        'Könnten Sie mir bitte erklären, wie es weitergeht?',
        'Could you explain to me what happens next, please?',
        'Another verb-to-the-end clause after wie. weitergehen is separable, so the prefix rejoins the verb at the end: weitergeht.',
      ],
      [
        'Ich habe das Formular ausgefüllt. Fehlt noch etwas?',
        'I have filled in the form. Is anything still missing?',
        'ausfüllen is separable, and in the perfect the ge- lands in the middle: aus-ge-füllt. "Fehlt noch etwas?" is the question that ends the visit cleanly.',
      ],
    ],
  },
]
